import { useEffect, useState } from 'react';
import classNames from 'classnames';

import { CELL_HEIGHT, CELL_WIDTH } from './VirtualizedPayoutTable';

import styles from '../payout-structures.module.css';

const TableCellRangeInput = ({
  onChange,
  onFocus,
  value,
  min = 0,
  invalid = false,
}: {
  onChange: (value: { from: number; to: number }) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  value: { from: number; to: number };
  min?: number;
  invalid?: boolean;
}) => {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (value.from === value.to) {
      setInputValue(value.from.toString());
      return;
    }

    setInputValue(`${value.from}-${value.to}`);
  }, [value]);

  /**
   * Handles input value changes and validates the range.
   * @param newValue - The input string, either a single number or a range (e.g., "5" or "1-10")
   *
   * Validation rules:
   * - For ranges: both numbers must be positive and lower bound must be <= upper bound
   * - For single values: must be a positive number
   * - Invalid inputs (negative, non-numeric, or inverted ranges) are rejected and onChange is not called
   */
  const handleChange = (newValue: string) => {
    const isRange = newValue.includes('-');

    if (isRange) {
      const [playersFrom, playersTo] = newValue.split('-');
      const fromNum = Number(playersFrom.trim());
      const toNum = Number(playersTo.trim());

      // Only update Redux if both values are valid numbers, positive, and ordered correctly
      if (!isNaN(fromNum) && !isNaN(toNum) && fromNum > 0 && toNum > 0 && fromNum <= toNum) {
        onChange({
          from: fromNum,
          to: toNum,
        });
      }
      return;
    }

    const num = Number(newValue.trim());
    // Only update Redux if the value is a valid number
    if (!isNaN(num) && num > 0) {
      onChange({
        from: num,
        to: num,
      });
    }
  };

  return (
    <input
      className={classNames(styles.payoutStructureTableInputCell, {
        [styles.invalidCell]: invalid,
      })}
      style={{
        height: CELL_HEIGHT,
        width: CELL_WIDTH,
      }}
      min={min}
      value={inputValue ?? ''}
      onChange={(e) => {
        const newValue = e.target.value;

        if (newValue === '0') {
          setInputValue('1');
          handleChange('1');
          return;
        }

        setInputValue(newValue);
        // Update Redux immediately on change
        handleChange(newValue);
      }}
      onBlur={(e) => {
        const newValue = e.target.value;
        // Try to update Redux with the current value
        handleChange(newValue);
        // Reset to last valid Redux value to clear any invalid input
        if (value.from === value.to) {
          setInputValue(value.from.toString());
        } else {
          setInputValue(`${value.from}-${value.to}`);
        }
      }}
      onFocus={onFocus ?? undefined}
    />
  );
};

export default TableCellRangeInput;
